/**
 * What a tool call reads as in the transcript, Cursor-style: one short line,
 * verb + target ("Read I-129.pdf", "Listed Evidence/", "Searched the draft
 * for "G-28""), present tense while running and past tense once done.
 *
 * Every case-agent tool has an entry; unknown tools fall back to a humanised
 * name plus their first short string argument. `kind` drives the layout:
 * consecutive `read` calls fold into one "Explored N files" line, `write`
 * calls stay on their own line, `run` calls (background work, subagents) get
 * a collapsible row, `own` calls render their purpose-built card.
 */
import {
  Activity,
  AlertTriangle,
  ArrowRightLeft,
  Bell,
  BookOpen,
  Bot,
  Briefcase,
  Building2,
  CalendarClock,
  ClipboardList,
  Clock,
  Copy,
  FileInput,
  FilePlus2,
  FileSearch,
  FileText,
  FlaskConical,
  FolderOpen,
  FolderPlus,
  Globe,
  Highlighter,
  ImageUp,
  Landmark,
  LayoutTemplate,
  CircleQuestionMark,
  ListChecks,
  ListTodo,
  Lock,
  Mail,
  Map as MapIcon,
  MessageCircleQuestion,
  Package,
  Paperclip,
  Pencil,
  PenLine,
  Plug,
  Receipt,
  Replace,
  Scale,
  Scissors,
  ScrollText,
  ScanSearch,
  Search,
  Send,
  Settings2,
  SkipForward,
  SlidersHorizontal,
  Sparkles,
  Star,
  Stamp,
  StickyNote,
  Terminal,
  Timer,
  Trash2,
  UserCog,
  Users,
  Wrench,
  Zap,
  type LucideIcon
} from "lucide-react";

import { withArticle } from "@/features/companies/utils";

import { friendlyRunFailure } from "../../workspace/workspaceModel";
import type { ArtifactKind } from "../../types";

export type ToolKind = "read" | "write" | "run" | "own";

/** The harness's own calls (loading deferred tools): mechanics, not work. */
const HARNESS_TOOLS: ReadonlySet<string> = new Set(["load_tools"]);

/** Whether the transcript leaves this call out. */
export const isHarnessTool = (name: string): boolean => HARNESS_TOOLS.has(name);

/** Resolves an artifact to its display title (the IDE knows names; the panel does not). */
export type ArtifactResolver = (
  kind: ArtifactKind,
  id: string
) => string | null;

type Input = Record<string, unknown>;

/** `[present, past]` verb phrases, fixed or chosen from the input and the result. */
type Verbs =
  | [string, string]
  | ((input: Input, output?: unknown) => [string, string]);

type Entry = {
  kind: ToolKind;
  icon: LucideIcon;
  verbs: Verbs;
  /** What the verbs act on when no target can be read off the input. */
  object?: string;
  /** The target read off the input (a file name, a query, a count). */
  target?: (input: Input, resolve: ArtifactResolver) => string | null;
  /** Reads that are searches count separately in the fold's title. */
  search?: boolean;
  /** A list: the finished row counts what it returned ("Listed 12 clients"). */
  counts?: boolean;
};

export type ToolTitle = {
  kind: ToolKind;
  icon: LucideIcon;
  /** "Reading I-129.pdf" */
  present: string;
  /** "Read I-129.pdf" */
  past: string;
  /** The target alone, when there was one. */
  target: string | null;
  search: boolean;
};

const isRecord = (v: unknown): v is Input =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const str = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v.trim() : null;

/**
 * The call only started its run: the result names a `run` but is not the
 * awaited final payload (`awaited: true`). The server decides whether a run
 * goes to the background, so before a result there is only the input's flag.
 */
const startedRun = (input: Input, output: unknown): boolean =>
  output === undefined
    ? input.run_in_background === true
    : isRecord(output) && isRecord(output.run) && output.awaited !== true;

/** "2026-12-01" as "December 1, 2026"; anything else as given. */
const dayInWords = (v: unknown): string | null => {
  const text = str(v);
  const m = text ? /^(\d{4})-(\d{2})-(\d{2})/.exec(text) : null;
  if (!m) return text;
  const day = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return day.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
};

const clip = (s: string, max: number): string =>
  s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;

// A `task` brief opens with a title line; older briefs fall back to their first sentence.
const taskTitle = (description: string): string => {
  const [first, ...rest] = description.split("\n");
  if (rest.length && first.trim()) return first.trim();
  const sentence = description.match(/^.+?[.!?](?=\s|$)/);
  return (sentence ? sentence[0] : description).trim();
};

const quoted = (v: unknown): string | null => {
  const s = str(v);
  return s ? `"${clip(s, 48)}"` : null;
};

const basename = (path: string): string => {
  const trimmed = path.replace(/\/+$/, "");
  return trimmed.split("/").pop() || trimmed;
};

const count = (v: unknown, noun: string): string | null => {
  if (!Array.isArray(v)) return null;
  const n = v.length;
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
};

const artifact =
  (kind: ArtifactKind, ...keys: string[]) =>
  (input: Input, resolve: ArtifactResolver): string | null => {
    for (const key of keys) {
      const id = str(input[key]);
      if (id) return resolve(kind, id) ?? null;
    }
    return null;
  };

/** A bare record id (a UUID or a Firestore id), which no one reads as a name. */
const looksLikeId = (t: string): boolean =>
  t.length >= 16 && /\d/.test(t) && /^[A-Za-z0-9_-]+$/.test(t);

/**
 * A PDF tool's `target`: the file's name when the IDE or the transcript knows
 * it, else the path's tail; an unknown id leaves the row to say "a PDF".
 */
const pdfTarget = (input: Input, resolve: ArtifactResolver): string | null => {
  const t = str(input.target);
  if (!t) return null;
  if (t.toLowerCase() === "packet") return "the packet";
  const name = resolve("file", t);
  if (name) return name;
  const tail = basename(t);
  return looksLikeId(tail) ? null : tail;
};

const plural = (n: number, word: string, many = `${word}s`): string =>
  `${n} ${n === 1 ? word : many}`;

const entryCount = (v: unknown): string | null =>
  Array.isArray(v) ? plural(v.length, "entry", "entries") : null;

const pdfOpsOf = (ops: unknown): Input[] =>
  Array.isArray(ops) ? ops.filter(isRecord) : [];

const lengthOf = (v: unknown): number => (Array.isArray(v) ? v.length : 0);

const pdfOpPhrase = (op: Input): string | null => {
  switch (op.op) {
    case "annotate": {
      const marks = pdfOpsOf(op.annotations);
      const types = new Set(marks.map((a) => str(a.type) ?? "mark"));
      const word = types.size === 1 ? [...types][0].replace(/_/g, " ") : "mark";
      return `add ${plural(marks.length, word)}`;
    }
    case "add_text":
      return `write ${plural(lengthOf(op.items), "text block")}`;
    case "redact":
      return `redact ${plural(
        lengthOf(op.search) + lengthOf(op.rects),
        "term or region",
        "terms and regions"
      )}`;
    case "stamp":
      return String(op.template ?? "").includes("{prefix}")
        ? "stamp Bates labels"
        : "stamp page numbers";
    case "watermark":
      return str(op.text)
        ? `watermark "${clip(str(op.text) as string, 32)}"`
        : "watermark";
    case "remove_annotations":
      return str(op.pages)
        ? `remove marks on pages ${op.pages}`
        : "remove marks";
    case "delete_pages":
      return `delete pages ${str(op.pages) ?? ""}`.trim();
    case "keep_pages":
      return `keep pages ${str(op.pages) ?? ""}`.trim();
    case "rotate":
      return `rotate pages ${str(op.pages) ?? "all"}`;
    case "insert_from_node":
      return "insert pages from another file";
    case "set_annotation_contents":
      return "comment on a mark";
    case "set_bookmarks":
      return "replace the bookmarks";
    default:
      return null;
  }
};

/**
 * What an `edit_pdf` call's ops do, as one sentence without the target:
 * "Redact 3 terms and regions, write 27 text blocks". Mirrors the backend's
 * `ops_title`, which the approval request carries.
 */
export const pdfOpsSummary = (ops: unknown): string | null => {
  const phrases = pdfOpsOf(ops)
    .map(pdfOpPhrase)
    .filter((p): p is string => Boolean(p));
  if (!phrases.length) return null;
  const text = phrases.join(", ");
  return `${text[0].toUpperCase()}${text.slice(1)}`;
};

const PDF_OP_VERBS: Record<string, [string, string]> = {
  annotate: ["Annotating", "Annotated"],
  set_annotation_contents: ["Commenting on", "Commented on"],
  add_text: ["Writing on", "Wrote on"],
  redact: ["Redacting", "Redacted"],
  stamp: ["Stamping", "Stamped"],
  watermark: ["Stamping", "Stamped"],
  remove_annotations: ["Removing annotations from", "Removed annotations from"],
  rotate: ["Editing pages of", "Edited pages of"],
  delete_pages: ["Editing pages of", "Edited pages of"],
  keep_pages: ["Editing pages of", "Edited pages of"],
  insert_from_node: ["Editing pages of", "Edited pages of"]
};

const editPdfVerbs = (input: Input): [string, string] => {
  if (input.dry_run) return ["Checking edits to", "Checked edits to"];
  if (str(input.batch_id))
    return ["Applying the checked edits to", "Applied the checked edits to"];
  const verbs = new Set(
    pdfOpsOf(input.ops).map(
      (op) => PDF_OP_VERBS[String(op.op)]?.join("|") ?? ""
    )
  );
  if (verbs.size === 1) {
    const [present, past] = [...verbs][0].split("|");
    if (present && past) return [present, past];
  }
  return ["Editing", "Edited"];
};

const fileTarget = (input: Input, resolve: ArtifactResolver): string | null =>
  artifact("file", "node_id")(input, resolve) ??
  (str(input.path) ? basename(str(input.path) as string) : null);

/**
 * `read_document`'s target grammar as a person reads it: "Exhibit B, pages
 * 3-5", "the packet, page 12", "the draft", "a Firm Knowledge document", "a
 * vault document". The IDE names case files; the rest read by their kind.
 */
const readDocumentTarget = (
  input: Input,
  resolve: ArtifactResolver
): string | null => {
  const t = str(input.target);
  if (!t) return null;
  const lower = t.toLowerCase();
  let what: string;
  if (lower === "packet") what = "the packet";
  else if (lower.startsWith("draft:")) {
    what = resolve("draft", t.slice("draft:".length).trim()) ?? "the draft";
  } else if (lower.startsWith("library:")) what = "a Firm Knowledge document";
  else if (lower.startsWith("attachment:")) what = "an attached file";
  else if (lower.startsWith("vault:")) what = "a vault document";
  else what = resolve("file", t) ?? basename(t);
  const pages = str(input.pages);
  if (!pages || lower.startsWith("draft:")) return what;
  const one = /^\d+$/.test(pages);
  return `${what}, ${one ? "page" : "pages"} ${clip(pages, 24)}`;
};

const AGENT_LABEL: Record<string, string> = {
  assistant: "Casey",
  // Handoff cards recorded before the merge.
  case_assistant: "Casey",
  client_manager: "Casey",
  practice_manager: "Casey"
};

const agentTarget = (input: Input): string | null => {
  const a = str(input.agent);
  return a ? AGENT_LABEL[a] ?? a.replace(/_/g, " ") : null;
};

const RUN_LABEL: Record<string, string> = {
  draft: "the draft",
  research: "the research memo",
  packet: "the packet build",
  form: "the form fill",
  export: "the filing export",
  autocategorize: "the organizer",
  pdf_edit: "the PDF edit"
};

/** Connected-app toolkit slugs whose display name is not just title case. */
const APP_NAMES: Record<string, string> = {
  gmail: "Gmail",
  googlecalendar: "Google Calendar",
  googledrive: "Google Drive",
  googlesheets: "Google Sheets",
  googledocs: "Google Docs",
  one_drive: "OneDrive",
  onedrive: "OneDrive",
  outlook: "Outlook",
  quickbooks: "QuickBooks",
  linkedin: "LinkedIn",
  github: "GitHub",
  hubspot: "HubSpot",
  docusign: "DocuSign"
};

const titleCase = (s: string): string =>
  s
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

/** "gmail" -> "Gmail", "googlecalendar" -> "Google Calendar", "zoom" -> "Zoom". */
export const appName = (slug: string): string =>
  APP_NAMES[slug.toLowerCase()] ?? titleCase(slug);

/**
 * Browser's one generic task tool. "Create" is the task record, not a change
 * in the account. Keep in step with `browser_task_is_read` in
 * `connected_apps.py`.
 */
const BROWSER_TASK_SLUG = "BROWSER_TOOL_CREATE_TASK";
const TASK_TEXT_KEYS = [
  "task",
  "prompt",
  "instruction",
  "query",
  "goal"
] as const;
const CHANGE_WORD =
  "(?:changes?|edits?|updates?|modifications?|writes?|creates?|creating|fills?|filling|submits?|submitting|deletes?|deleting|saves?|saving|removes?|removing)";

const negatedChange = (flags = "i"): RegExp =>
  new RegExp(
    `\\b(?:without|do not|does not|don['’]t|doesn['’]t|never|avoid|not|no)\\b(?:\\s+(?:making|make|any|a|or|the|and))*\\s+${CHANGE_WORD}(?:\\s+or\\s+${CHANGE_WORD})*`,
    flags
  );

const writeAction = (): RegExp =>
  /\b(?:fill|filling|fills|submit|submitting|submits|send|sending|sends|post|posting|posted|delete|deleting|deletes|remove|removing|removes|update|updating|updates|edit|editing|edits|modify|modifying|upload|uploading|save|saving|saves|create|creating|creates|add|adding|adds|change|changing|changes|alter|altering|write|writing|writes|replace|replacing|rename|renaming|publish|publishing|apply|applying)\b/i;

const inspectCue = (): RegExp => /\binspect(?:ing|ed|s|ion)?\b/i;

export const isBrowserTaskSlug = (slug: string): boolean =>
  slug.trim().toUpperCase() === BROWSER_TASK_SLUG;

/** The task the browser will run, plus the sentence the attorney sees. */
export const browserTaskText = (input: unknown): string => {
  if (!isRecord(input)) return "";
  const parts: string[] = [];
  const purpose = str(input.purpose);
  if (purpose) parts.push(purpose);
  if (!Array.isArray(input.tools)) return parts.join("\n");
  for (const tool of input.tools) {
    if (isRecord(tool) && isRecord(tool.arguments)) {
      for (const key of TASK_TEXT_KEYS) {
        const value = str(tool.arguments[key]);
        if (value) parts.push(value);
      }
    }
  }
  return parts.join("\n");
};

/**
 * An inspect, or a task that says it will not make changes, unless that same
 * text also creates, edits, fills, or submits. Empty text is a write.
 */
export const browserTaskIsRead = (text: string): boolean => {
  const raw = text.trim();
  if (!raw) return false;
  const stripped = raw.replace(negatedChange("gi"), " ");
  if (writeAction().test(stripped)) return false;
  if (negatedChange().test(raw)) return true;
  return inspectCue().test(raw);
};

/**
 * A connected-app tool slug as a person reads it: `GMAIL_FETCH_EMAILS` ->
 * "Gmail · Fetch emails", `GOOGLECALENDAR_LIST_EVENTS` -> "Google Calendar ·
 * List events". Browser's generic create-task tool is just "Browser": the
 * slug's "Tool create task" is not an action.
 */
export const appToolLabel = (slug: string): string => {
  const trimmed = slug.trim();
  if (isBrowserTaskSlug(trimmed)) return "Browser";
  const [toolkit, ...rest] = trimmed.split("_");
  if (!toolkit) return slug;
  const action = rest.join(" ").toLowerCase();
  const verb = action ? action[0].toUpperCase() + action.slice(1) : "";
  return verb ? `${appName(toolkit)} · ${verb}` : appName(toolkit);
};

const appNames = (v: unknown): string | null => {
  if (!Array.isArray(v)) return null;
  const names = v
    .filter((s): s is string => typeof s === "string")
    .map(appName);
  if (!names.length) return null;
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

/** The first app tool slug of a `run_app_tools` call, plus how many more ran. */
const appToolsTarget = (i: Input): string | null => {
  if (!Array.isArray(i.tools) || !i.tools.length) return str(i.purpose);
  const slugs = i.tools
    .map((t) => (isRecord(t) ? str(t.tool_slug) : null))
    .filter((s): s is string => Boolean(s));
  if (!slugs.length) return str(i.purpose);
  const more = slugs.length - 1;
  return more > 0
    ? `${appToolLabel(slugs[0])} and ${more} more`
    : appToolLabel(slugs[0]);
};

/** The settings tools' `section` argument as the transcript names it. */
const SETTINGS_SECTION_NAMES: Record<string, string> = {
  notifications: "notifications",
  agents: "agent model",
  time_capture: "time capture",
  details: "firm details",
  instructions: "firm instructions",
  house_style: "house style",
  case_preparation: "case preparation",
  approvals: "approvals and events",
  time_billing: "time and billing",
  permissions: "permissions"
};

const ENTRIES: Record<string, Entry> = {
  // --- Case files --------------------------------------------------------
  list_tree: {
    kind: "read",
    icon: FolderOpen,
    verbs: ["Listing", "Listed"],
    object: "the case files",
    target: artifact("file", "parent_id"),
    counts: true
  },
  read_file_text: {
    kind: "read",
    icon: FileText,
    verbs: ["Reading", "Read"],
    object: "a document",
    target: fileTarget
  },
  read_document: {
    kind: "read",
    icon: BookOpen,
    verbs: ["Reading", "Read"],
    object: "a document",
    target: readDocumentTarget
  },
  // --- Files attached in the chat ------------------------------------------
  read_attachment_text: {
    kind: "read",
    icon: Paperclip,
    verbs: ["Reading", "Read"],
    object: "an attached file"
  },
  save_attachments_to_case: {
    kind: "write",
    icon: Paperclip,
    verbs: ["Saving", "Saved"],
    object: "the attached files to the case",
    target: (i) => {
      const n = count(i.attachment_ids, "file");
      const folder = str(i.folder);
      if (n && folder) return `${n} to ${folder}`;
      return n ? `${n} to the case` : folder ? `files to ${folder}` : null;
    }
  },
  save_attachments_to_client: {
    kind: "write",
    icon: Paperclip,
    verbs: ["Adding", "Added"],
    object: "the attached files to the client's documents",
    target: (i) => {
      const n = count(i.attachment_ids, "file");
      return n ? `${n} to the client's documents` : null;
    }
  },
  set_client_photo: {
    kind: "write",
    icon: ImageUp,
    verbs: ["Setting", "Set"],
    object: "the client's photo"
  },
  send_attachments_to_case: {
    kind: "write",
    icon: Send,
    verbs: ["Saving", "Saved"],
    object: "the attached files to a case",
    target: (i) => {
      const n = count(i.attachment_ids, "file");
      return n ? `${n} to a case` : null;
    }
  },
  save_attachment_to_firm_library: {
    kind: "write",
    icon: BookOpen,
    verbs: ["Adding", "Added"],
    object: "an attached file to the library",
    target: (i) => {
      const name = str(i.name);
      return name ? `${name} to the library` : null;
    }
  },
  search_case_text: {
    kind: "read",
    icon: FileSearch,
    search: true,
    verbs: ["Searching files for", "Searched files for"],
    object: "text",
    target: (i) => quoted(i.query)
  },
  create_folder: {
    kind: "write",
    icon: FolderPlus,
    verbs: ["Creating folder", "Created folder"],
    target: (i) => str(i.name)
  },
  rename_node: {
    kind: "write",
    icon: Pencil,
    verbs: ["Renaming", "Renamed"],
    object: "a file",
    target: (i, r) => {
      const to = str(i.name);
      const from = artifact("file", "node_id")(i, r);
      if (from && to) return `${from} to ${to}`;
      return to ? `to ${to}` : from;
    }
  },
  set_exhibit_titles: {
    kind: "write",
    icon: Pencil,
    verbs: ["Retitling", "Retitled"],
    object: "files",
    target: (i) =>
      count(i.titles, "file") ??
      (str(i.proposal_id) ? "the proposed titles" : null)
  },
  keep_exhibit_titles: {
    kind: "write",
    icon: Pencil,
    verbs: ["Keeping the titles of", "Kept the titles of"],
    object: "files",
    target: (i) => count(i.node_ids, "file")
  },
  propose_exhibit_titles: {
    kind: "run",
    icon: Sparkles,
    verbs: ["Proposing titles for", "Proposed titles for"],
    object: "files",
    target: (i) =>
      str(i.scope) === "folder"
        ? "a folder"
        : count(i.node_ids, "file") ?? "files that need them"
  },
  check_filing_readiness: {
    kind: "read",
    icon: ListChecks,
    verbs: ["Checking", "Checked"],
    object: "filing readiness"
  },
  case_health: {
    kind: "read",
    icon: ListChecks,
    verbs: (i) =>
      str(i.focus)
        ? ["Checking what depends on", "Checked what depends on"]
        : ["Checking", "Checked"],
    object: "case health",
    target: (i, resolve) => {
      const focus = str(i.focus);
      return focus
        ? resolve("file", focus) ?? resolve("draft", focus) ?? focus
        : null;
    }
  },
  offer_action: {
    kind: "own",
    icon: Sparkles,
    verbs: ["Offering", "Offered"],
    object: "to do more",
    target: (i) => str(i.label)
  },
  // --- Support ------------------------------------------------------------
  read_app_guide: {
    kind: "read",
    icon: BookOpen,
    verbs: ["Checking the CaseBlink guide", "Checked the CaseBlink guide"]
  },
  contact_support: {
    kind: "own",
    icon: CircleQuestionMark,
    verbs: ["Writing a note for support", "Wrote a note for support"]
  },
  move_nodes: {
    kind: "write",
    icon: ArrowRightLeft,
    verbs: ["Moving", "Moved"],
    object: "files",
    target: (i) => count(i.node_ids, "file")
  },
  delete_nodes: {
    kind: "write",
    icon: Trash2,
    verbs: ["Deleting", "Deleted"],
    object: "files",
    target: (i) => count(i.node_ids, "file")
  },
  copy_case_files: {
    kind: "write",
    icon: Copy,
    verbs: ["Copying", "Copied"],
    object: "files from another case",
    target: (i) => {
      const n = count(i.node_ids, "file");
      return n ? `${n} from another case` : null;
    }
  },
  set_packet_inclusion: {
    kind: "write",
    icon: Package,
    verbs: (i) =>
      i.included === false
        ? ["Excluding from the packet", "Excluded from the packet"]
        : ["Including in the packet", "Included in the packet"],
    object: "files",
    target: (i) => count(i.node_ids, "file")
  },
  mark_internal: {
    kind: "write",
    icon: Lock,
    verbs: ["Marking internal", "Marked internal"],
    object: "files",
    target: (i) => count(i.node_ids, "file")
  },
  set_exhibit_numbering: {
    kind: "write",
    icon: ListChecks,
    verbs: (i) =>
      i.numbered === false
        ? ["Removing exhibit numbers from", "Removed exhibit numbers from"]
        : ["Numbering", "Numbered"],
    object: "exhibits",
    target: (i) => count(i.node_ids, "exhibit")
  },
  set_exhibit_label: {
    kind: "write",
    icon: ListChecks,
    verbs: ["Relabeling", "Relabeled"],
    object: "an exhibit",
    target: (i, r) => {
      const label = str(i.label);
      const file = artifact("file", "node_id")(i, r);
      if (file && label) return `${file} as Exhibit ${label}`;
      return label ? `as Exhibit ${label}` : file;
    }
  },
  promote_to_case_files: {
    kind: "write",
    icon: FileInput,
    verbs: ["Adding", "Added"],
    // Done, the row reads the result instead ("Added X to Case Files as Exhibit B.5").
    target: (i, r) => {
      const source = str(i.source)?.replace(/^(draft|form):/, "");
      const name = source
        ? r("draft", source) ?? r("form", source) ?? null
        : null;
      return `${name ?? "a work product"} to Case Files`;
    }
  },
  set_exhibit_scheme: {
    kind: "write",
    icon: Settings2,
    verbs: ["Changing the exhibit scheme", "Changed the exhibit scheme"]
  },
  start_autocategorize: {
    kind: "run",
    icon: Sparkles,
    // A waited-on run shows its steps under the row; a background start is
    // merely "started".
    verbs: (i, o) =>
      startedRun(i, o)
        ? ["Starting the organizer", "Started the organizer"]
        : ["Running the organizer", "Ran the organizer"]
  },
  get_autocategorize_plan: {
    kind: "read",
    icon: Sparkles,
    verbs: ["Checking the organizer's plan", "Checked the organizer's plan"]
  },
  apply_autocategorize_plan: {
    kind: "write",
    icon: Sparkles,
    verbs: ["Applying the organizer's plan", "Applied the organizer's plan"]
  },
  cancel_autocategorize: {
    kind: "write",
    icon: Sparkles,
    verbs: ["Cancelling the organizer", "Cancelled the organizer"]
  },
  // --- Drafts -----------------------------------------------------------
  list_drafts: {
    kind: "read",
    icon: FileText,
    verbs: ["Listing drafts", "Listed drafts"]
  },
  create_draft: {
    kind: "write",
    icon: FilePlus2,
    verbs: ["Creating draft", "Created draft"],
    target: (i) => str(i.title) ?? str(i.draft_type)?.replace(/_/g, " ") ?? null
  },
  generate_draft: {
    kind: "run",
    icon: PenLine,
    // In chat the server usually starts the drafting agent in the background
    // and returns at once; only an awaited result is the finished draft.
    verbs: (i, o) =>
      startedRun(i, o)
        ? i.revise
          ? ["Revising", "Started revising"]
          : ["Drafting", "Started drafting"]
        : i.revise
        ? ["Revising", "Revised"]
        : ["Drafting", "Drafted"],
    object: "a draft",
    target: artifact("draft", "draft_id")
  },
  get_draft: {
    kind: "read",
    icon: FileText,
    verbs: ["Checking", "Checked"],
    object: "a draft",
    target: artifact("draft", "draft_id")
  },
  read_draft_text: {
    kind: "read",
    icon: FileText,
    verbs: ["Reading", "Read"],
    object: "a draft",
    target: artifact("draft", "draft_id")
  },
  read_draft_document: {
    kind: "read",
    icon: FileText,
    verbs: ["Reading", "Read"],
    object: "the document",
    target: artifact("draft", "draft_id")
  },
  find_in_draft: {
    kind: "read",
    icon: FileSearch,
    search: true,
    verbs: ["Searching", "Searched"],
    object: "the draft",
    target: (i, r) => {
      const where = artifact("draft", "draft_id")(i, r) ?? "the draft";
      const q = quoted(i.query);
      return q ? `${where} for ${q}` : where;
    }
  },
  update_draft_from_prose: {
    kind: "write",
    icon: PenLine,
    verbs: ["Writing", "Wrote"],
    object: "a draft",
    target: (i, r) => str(i.title) ?? artifact("draft", "draft_id")(i, r)
  },
  edit_draft: {
    kind: "write",
    icon: Pencil,
    verbs: ["Editing", "Edited"],
    object: "the document",
    target: artifact("draft", "draft_id")
  },
  propose_rewrite: {
    kind: "write",
    icon: Pencil,
    verbs: ["Proposing a rewrite in", "Proposed a rewrite in"],
    object: "the document",
    target: artifact("draft", "draft_id")
  },
  suggest_change: {
    kind: "write",
    icon: Pencil,
    verbs: ["Suggesting a change in", "Suggested a change in"],
    object: "the document",
    target: artifact("draft", "draft_id")
  },
  comment_on_draft: {
    kind: "write",
    icon: PenLine,
    verbs: ["Commenting on", "Commented on"],
    object: "the document",
    target: artifact("draft", "draft_id")
  },
  list_draft_changes: {
    kind: "read",
    icon: ListChecks,
    verbs: ["Checking pending changes in", "Checked pending changes in"],
    object: "the document",
    target: artifact("draft", "draft_id")
  },
  resolve_draft_changes: {
    kind: "write",
    icon: ListChecks,
    verbs: (i) =>
      i.action === "accept_all"
        ? [
            "Accepting all tracked changes in",
            "Accepted all tracked changes in"
          ]
        : [
            "Rejecting all tracked changes in",
            "Rejected all tracked changes in"
          ],
    object: "the document",
    target: artifact("draft", "draft_id")
  },
  delete_draft: {
    kind: "write",
    icon: Trash2,
    verbs: ["Deleting", "Deleted"],
    object: "a draft",
    target: artifact("draft", "draft_id")
  },
  // --- Packet -----------------------------------------------------------
  get_packet: {
    kind: "read",
    icon: Package,
    verbs: ["Checking the packet", "Checked the packet"]
  },
  set_packet_options: {
    kind: "write",
    icon: Settings2,
    verbs: ["Saving packet options", "Saved packet options"]
  },
  set_divider_title: {
    kind: "write",
    icon: Pencil,
    verbs: ["Retitling", "Retitled"],
    object: "a tab",
    target: (i) => {
      const label = str(i.label);
      return label ? `the tab of ${label}` : null;
    }
  },
  generate_packet: {
    kind: "run",
    icon: Package,
    verbs: (_i, o) =>
      isRecord(o) && o.awaited === true
        ? ["Assembling the packet", "Assembled the packet"]
        : ["Assembling the packet", "Started assembling the packet"]
  },
  export_filing_materials: {
    kind: "run",
    icon: Package,
    verbs: (_i, o) =>
      isRecord(o) && o.awaited === true
        ? ["Exporting filing materials", "Exported filing materials"]
        : ["Exporting filing materials", "Started the filing export"]
  },
  // --- PDFs -------------------------------------------------------------
  inspect_pdf: {
    kind: "read",
    icon: FileSearch,
    verbs: ["Inspecting", "Inspected"],
    object: "a PDF",
    target: pdfTarget
  },
  start_pdf_edit: {
    kind: "run",
    icon: Wrench,
    // Waits on the PDF agent by default (Cursor Task semantics); only a
    // background run is reported as merely started.
    verbs: (i, o) =>
      startedRun(i, o)
        ? ["Starting a PDF edit on", "Started a PDF edit on"]
        : ["Editing", "Edited"],
    object: "a file",
    target: pdfTarget
  },
  get_pdf_edit_run: {
    kind: "read",
    icon: Wrench,
    verbs: ["Checking a PDF edit", "Checked a PDF edit"]
  },
  list_pdf_annotations: {
    kind: "read",
    icon: Highlighter,
    verbs: ["Reading annotations in", "Read annotations in"],
    object: "a PDF",
    target: pdfTarget
  },
  edit_pdf: {
    kind: "write",
    icon: Highlighter,
    verbs: editPdfVerbs,
    object: "a PDF",
    target: pdfTarget
  },
  draft_pdf_changes: {
    kind: "run",
    icon: Sparkles,
    verbs: (i) =>
      str(i.kind) === "highlight"
        ? ["Drafting highlights for", "Drafted highlights for"]
        : ["Drafting tab text for", "Drafted tab text for"],
    object: "the exhibits",
    target: (i, resolve) =>
      str(i.target) && str(i.target) !== "packet"
        ? pdfTarget(i, resolve)
        : "the packet's exhibits"
  },
  edit_pdf_pages: {
    kind: "write",
    icon: Scissors,
    verbs: ["Editing pages of", "Edited pages of"],
    object: "a PDF",
    target: pdfTarget
  },
  suggest_case_file_splits: {
    kind: "read",
    icon: Scissors,
    verbs: ["Finding split points in", "Found split points in"],
    object: "a PDF",
    target: pdfTarget
  },
  split_case_file: {
    kind: "write",
    icon: Scissors,
    verbs: ["Splitting", "Split"],
    object: "a PDF",
    target: pdfTarget
  },
  merge_case_files: {
    kind: "write",
    icon: Scissors,
    verbs: ["Merging", "Merged"],
    object: "files",
    target: (i) => count(i.sources, "file")
  },
  annotate_pdf: {
    kind: "write",
    icon: Highlighter,
    verbs: ["Annotating", "Annotated"],
    object: "a PDF",
    target: pdfTarget
  },
  comment_on_pdf_annotation: {
    kind: "write",
    icon: Highlighter,
    verbs: ["Commenting on", "Commented on"],
    object: "a PDF annotation",
    target: pdfTarget
  },
  remove_pdf_annotations: {
    kind: "write",
    icon: Highlighter,
    verbs: ["Removing annotations from", "Removed annotations from"],
    object: "a PDF",
    target: pdfTarget
  },
  redact_pdf: {
    kind: "write",
    icon: Scissors,
    verbs: ["Redacting", "Redacted"],
    object: "a PDF",
    target: pdfTarget
  },
  stamp_pdf: {
    kind: "write",
    icon: Stamp,
    verbs: ["Stamping", "Stamped"],
    object: "a PDF",
    target: pdfTarget
  },
  // --- Forms ------------------------------------------------------------
  list_form_catalog: {
    kind: "read",
    icon: FileInput,
    verbs: ["Browsing the form catalog", "Browsed the form catalog"]
  },
  list_forms: {
    kind: "read",
    icon: FileInput,
    verbs: ["Listing forms", "Listed forms"]
  },
  list_form_sources: {
    kind: "read",
    icon: FileInput,
    verbs: ["Checking form sources", "Checked form sources"]
  },
  create_form: {
    kind: "write",
    icon: FilePlus2,
    verbs: ["Creating form", "Created form"],
    target: (i) => str(i.form_number)
  },
  fill_form: {
    kind: "run",
    icon: FileInput,
    verbs: ["Filling", "Filled"],
    object: "a form",
    target: (i, r) => str(i.form_number) ?? artifact("form", "form_id")(i, r)
  },
  get_form: {
    kind: "read",
    icon: FileInput,
    verbs: ["Checking", "Checked"],
    object: "a form",
    target: artifact("form", "form_id")
  },
  // --- Case, runs, research --------------------------------------------
  get_case_summary: {
    kind: "read",
    icon: FileText,
    verbs: ["Reading the case summary", "Read the case summary"]
  },
  update_case_status: {
    kind: "write",
    icon: Settings2,
    verbs: ["Setting the case status", "Set the case status"],
    target: (i) => (str(i.status) ? `to ${str(i.status)}` : null)
  },
  update_case_deadline: {
    kind: "write",
    icon: CalendarClock,
    verbs: (i) =>
      i.filing_deadline
        ? ["Setting the filing deadline", "Set the filing deadline"]
        : ["Clearing the filing deadline", "Cleared the filing deadline"],
    target: (i) =>
      dayInWords(i.filing_deadline)
        ? `to ${dayInWords(i.filing_deadline)}`
        : null
  },
  check_run: {
    kind: "read",
    icon: ClipboardList,
    // With `block_until_seconds` it waits on the job (Cursor AwaitShell).
    verbs: (i) =>
      typeof i.block_until_seconds === "number" && i.block_until_seconds > 0
        ? ["Waiting on", "Waited on"]
        : ["Checking on", "Checked on"],
    object: "background work",
    target: (i) =>
      str(i.kind) ? RUN_LABEL[str(i.kind) as string] ?? null : null
  },
  // --- USCIS status ------------------------------------------------------
  get_uscis_status: {
    kind: "read",
    icon: Activity,
    verbs: ["Checking USCIS status", "Checked USCIS status"],
    target: (i) => {
      const n = count(i.receipt_numbers, "receipt");
      return n ? `for ${n}` : null;
    }
  },
  track_uscis_receipts: {
    kind: "write",
    icon: Activity,
    verbs: ["Tracking", "Tracked"],
    object: "USCIS receipts",
    target: (i) => count(i.receipt_numbers, "USCIS receipt")
  },
  untrack_uscis_receipt: {
    kind: "write",
    icon: Trash2,
    verbs: ["Untracking USCIS receipt", "Untracked USCIS receipt"],
    target: (i) => str(i.receipt_number)
  },
  start_research: {
    kind: "run",
    icon: FlaskConical,
    verbs: (i, o) =>
      startedRun(i, o)
        ? ["Researching", "Started research on"]
        : ["Researching", "Researched"],
    object: "a question",
    target: (i) => quoted(i.question)
  },
  start_standing_research: {
    kind: "run",
    icon: FlaskConical,
    verbs: (i) =>
      i.subject_coverage
        ? [
            "Starting reputation and coverage research",
            "Started reputation and coverage research"
          ]
        : ["Starting reputation research", "Started reputation research"]
  },
  get_research_run: {
    kind: "read",
    icon: FlaskConical,
    verbs: ["Checking research", "Checked research"]
  },
  // --- Research sources (inline lookups) --------------------------------
  search_precedent_decisions: {
    kind: "read",
    icon: Scale,
    verbs: ["Searching AAO precedent", "Searched AAO precedent"],
    target: (i) => quoted(i.query),
    search: true
  },
  search_non_precedent_decisions: {
    kind: "read",
    icon: Scale,
    verbs: [
      "Searching non-precedent decisions",
      "Searched non-precedent decisions"
    ],
    target: (i) => quoted(i.query),
    search: true
  },
  search_policy_manual: {
    kind: "read",
    icon: BookOpen,
    verbs: ["Searching the Policy Manual", "Searched the Policy Manual"],
    target: (i) => quoted(i.query),
    search: true
  },
  search_bia_decisions: {
    kind: "read",
    icon: Scale,
    verbs: ["Searching BIA precedent", "Searched BIA precedent"],
    target: (i) => quoted(i.query),
    search: true
  },
  search_govinfo: {
    kind: "read",
    icon: Landmark,
    verbs: ["Searching GovInfo", "Searched GovInfo"],
    target: (i) => quoted(i.query),
    search: true
  },
  find_occupation: {
    kind: "read",
    icon: Briefcase,
    verbs: ["Looking up occupation", "Looked up occupation"],
    target: (i) => quoted(i.job_title)
  },
  lookup_prevailing_wage: {
    kind: "read",
    icon: Briefcase,
    verbs: ["Looking up prevailing wage", "Looked up prevailing wage"],
    target: (i) => {
      const occupation = str(i.occupation);
      const location = str(i.location);
      return occupation && location
        ? `for ${clip(occupation, 32)} in ${clip(location, 32)}`
        : occupation
        ? `for ${clip(occupation, 48)}`
        : null;
    }
  },
  get_occupation_outlook: {
    kind: "read",
    icon: Briefcase,
    verbs: ["Reading occupation outlook", "Read occupation outlook"],
    target: (i) => str(i.onet_code)
  },
  lookup_journal_ranking: {
    kind: "read",
    icon: BookOpen,
    verbs: ["Looking up journal ranking", "Looked up journal ranking"],
    target: (i) => quoted(i.journal)
  },
  lookup_scholar_venue: {
    kind: "read",
    icon: BookOpen,
    verbs: ["Looking up Scholar metrics", "Looked up Scholar metrics"],
    target: (i) => quoted(i.venue)
  },
  lookup_stem_program: {
    kind: "read",
    icon: FlaskConical,
    verbs: ["Checking the STEM list", "Checked the STEM list"],
    target: (i) => quoted(i.field)
  },
  lookup_cet_area: {
    kind: "read",
    icon: FlaskConical,
    verbs: ["Checking the CET list", "Checked the CET list"],
    target: (i) => quoted(i.field)
  },
  list_cisa_sectors: {
    kind: "read",
    icon: FlaskConical,
    verbs: (i) =>
      str(i.field)
        ? ["Matching CISA sectors", "Matched CISA sectors"]
        : ["Listing CISA sectors", "Listed CISA sectors"],
    target: (i) => quoted(i.field)
  },
  lookup_usmca_profession: {
    kind: "read",
    icon: Briefcase,
    verbs: ["Checking USMCA professions", "Checked USMCA professions"],
    target: (i) => quoted(i.profession)
  },
  get_country_conditions: {
    kind: "read",
    icon: Globe,
    verbs: ["Reading country conditions", "Read country conditions"],
    target: (i) => str(i.country)
  },
  web_search: {
    kind: "read",
    icon: Globe,
    verbs: ["Searching the web", "Searched the web"],
    target: (i) => quoted(i.query),
    search: true
  },
  fetch_page: {
    kind: "read",
    icon: FileText,
    verbs: ["Reading a page", "Read a page"],
    target: (i) => str(i.url)
  },
  // --- Connected apps (Gmail, Calendar, Drive, Slack, ...) --------------
  search_app_tools: {
    kind: "read",
    icon: Plug,
    search: true,
    verbs: ["Searching connected apps for", "Searched connected apps for"],
    object: "tools",
    target: (i) =>
      Array.isArray(i.queries) ? quoted(i.queries[0]) : quoted(i.queries)
  },
  get_app_tool_schemas: {
    kind: "read",
    icon: Plug,
    verbs: ["Checking", "Checked"],
    object: "an app tool",
    target: (i) => {
      const slug = Array.isArray(i.tool_slugs) ? str(i.tool_slugs[0]) : null;
      return slug ? appToolLabel(slug) : null;
    }
  },
  run_app_tools: {
    // Executes in the attorney's apps: its own line, never folded into a read.
    kind: "write",
    icon: Zap,
    verbs: ["Running", "Ran"],
    object: "app tools",
    target: appToolsTarget
  },
  connect_apps: {
    kind: "own",
    icon: Plug,
    verbs: ["Connecting", "Connected"],
    object: "an app",
    target: (i) => appNames(i.toolkits)
  },
  // --- Plan mode (own cards) -------------------------------------------
  create_plan: {
    kind: "own",
    icon: MapIcon,
    verbs: ["Writing a plan", "Wrote a plan"]
  },
  update_plan: {
    kind: "own",
    icon: MapIcon,
    verbs: ["Revising the plan", "Revised the plan"]
  },
  update_todos: {
    kind: "own",
    icon: ListTodo,
    verbs: ["Updating the todo list", "Updated the todo list"]
  },
  load_tools: {
    kind: "read",
    icon: Wrench,
    verbs: ["Getting the right tools", "Got the right tools"]
  },
  write_todos: {
    kind: "write",
    icon: ListTodo,
    verbs: ["Updating the todo list", "Updated the todo list"]
  },
  ask_user: {
    kind: "own",
    icon: MessageCircleQuestion,
    verbs: ["Asking you a question", "Asked you a question"]
  },
  request_mode_switch: {
    kind: "write",
    icon: ArrowRightLeft,
    verbs: ["Proposing a switch to", "Proposed a switch to"],
    object: "another mode",
    target: (i) =>
      str(i.mode) === "plan"
        ? "Plan mode"
        : str(i.mode) === "agent"
        ? "Agent mode"
        : null
  },
  // --- Subagents and other agents -------------------------------------
  task: {
    kind: "run",
    icon: Bot,
    verbs: ["Exploring:", "Explored:"],
    object: "the case",
    target: (i) => {
      const description = str(i.description);
      return description ? clip(taskTitle(description), 72) : null;
    }
  },
  delegate_to_assistant: {
    kind: "run",
    icon: Users,
    verbs: ["Working on", "Worked on"],
    object: "a client or case",
    target: (i) => {
      const kind = str(i.scope_kind);
      if (!kind) return null;
      return kind === "case" ? "a case" : "a client";
    }
  },
  // Recorded before the merge; kept so old transcripts still read.
  ask_agent: {
    kind: "own",
    icon: Users,
    verbs: ["Asking", "Asked"],
    object: "another agent",
    target: agentTarget
  },
  handoff_to_agent: {
    kind: "run",
    icon: ArrowRightLeft,
    verbs: ["Handing off to", "Handed off to"],
    object: "another agent",
    target: agentTarget
  },
  // --- deepagents built-ins (subagent scratch files) --------------------
  ls: {
    kind: "read",
    icon: FolderOpen,
    verbs: ["Listing", "Listed"],
    object: "files",
    target: (i) => (str(i.path) ? `${basename(str(i.path) as string)}/` : null)
  },
  read_file: {
    kind: "read",
    icon: FileText,
    verbs: ["Reading", "Read"],
    object: "a file",
    target: (i) => {
      const path = str(i.file_path);
      if (!path) return null;
      const skill = /\/skills\/(?:[^/]+\/)*?([^/]+)\/SKILL\.md$/i.exec(path);
      return skill
        ? `the ${titleCase(skill[1].replace(/-/g, " "))} automation`
        : basename(path);
    }
  },
  write_file: {
    kind: "write",
    icon: FilePlus2,
    verbs: ["Writing", "Wrote"],
    object: "a file",
    target: (i) =>
      str(i.file_path) ? basename(str(i.file_path) as string) : null
  },
  edit_file: {
    kind: "write",
    icon: Pencil,
    verbs: ["Editing", "Edited"],
    object: "a file",
    target: (i) =>
      str(i.file_path) ? basename(str(i.file_path) as string) : null
  },
  glob: {
    kind: "read",
    icon: Search,
    search: true,
    verbs: ["Searching for", "Searched for"],
    object: "files",
    target: (i) => str(i.pattern)
  },
  grep: {
    kind: "read",
    icon: Search,
    search: true,
    verbs: ["Searching for", "Searched for"],
    object: "text",
    target: (i) => quoted(i.pattern) ?? quoted(i.query)
  },
  execute: {
    kind: "run",
    icon: Terminal,
    verbs: ["Running", "Ran"],
    object: "a command",
    target: (i) => {
      const cmd = str(i.command);
      return cmd ? clip(cmd.split("\n")[0], 64) : null;
    }
  },
  // --- Delegated agents' tools (drafting / research / organizer runs) --
  // Shown as rows under the delegating call while it waits on the run.
  list_case_files: {
    kind: "read",
    icon: FolderOpen,
    verbs: ["Listing", "Listed"],
    object: "the case files",
    target: (i) =>
      str(i.prefix) ? `${basename(str(i.prefix) as string)}/` : null
  },
  read_case_file: {
    kind: "read",
    icon: FileText,
    verbs: ["Reading", "Read"],
    object: "a case file",
    target: (i) => (str(i.path) ? basename(str(i.path) as string) : null)
  },
  get_exhibit_list: {
    kind: "read",
    icon: ListChecks,
    verbs: ["Reading the exhibit list", "Read the exhibit list"]
  },
  suggested_research_questions: {
    kind: "read",
    icon: FlaskConical,
    verbs: [
      "Checking suggested research questions",
      "Checked suggested research questions"
    ]
  },
  fetch_firm_document: {
    kind: "read",
    icon: Briefcase,
    verbs: ["Fetching", "Fetched"],
    object: "a firm document",
    target: (i) => (str(i.path) ? basename(str(i.path) as string) : null)
  },
  fetch_draft_docx: {
    kind: "read",
    icon: FileText,
    verbs: ["Fetching the current draft", "Fetched the current draft"]
  },
  fetch_case_pdf: {
    kind: "read",
    icon: FileText,
    verbs: ["Fetching", "Fetched"],
    object: "a PDF",
    target: (i) => (str(i.target) ? basename(str(i.target) as string) : null)
  },
  fetch_packet_pdf: {
    kind: "read",
    icon: Package,
    verbs: ["Fetching the packet PDF", "Fetched the packet PDF"]
  },
  get_visa_structure: {
    kind: "read",
    icon: MapIcon,
    verbs: [
      "Reading the visa's folder structure",
      "Read the visa's folder structure"
    ]
  },
  get_case_tree: {
    kind: "read",
    icon: FolderOpen,
    verbs: ["Reading the case tree", "Read the case tree"]
  },
  propose_categorization: {
    kind: "write",
    icon: Sparkles,
    verbs: ["Proposing a folder structure", "Proposed a folder structure"],
    target: (i) => {
      const plan = isRecord(i.plan) ? i.plan : null;
      return plan ? count(plan.folders, "folder") : null;
    }
  },
  submit_draft: {
    kind: "write",
    icon: Send,
    verbs: ["Submitting", "Submitted"],
    object: "the draft",
    target: (i) =>
      str(i.docx_path) ? basename(str(i.docx_path) as string) : null
  },
  submit_pdf: {
    kind: "write",
    icon: Send,
    verbs: ["Submitting", "Submitted"],
    object: "the PDF",
    target: (i) =>
      str(i.pdf_path) ? basename(str(i.pdf_path) as string) : null
  },
  report_failure: {
    kind: "write",
    icon: AlertTriangle,
    verbs: ["Reporting a failure", "Reported a failure"],
    target: (i) => quoted(i.reason)
  },
  find_wage_area: {
    kind: "read",
    icon: Briefcase,
    verbs: ["Looking up the wage area", "Looked up the wage area"],
    target: (i) =>
      str(i.location) ? `for ${clip(str(i.location) as string, 48)}` : null
  },
  // --- Client manager -------------------------------------------------
  get_client_profile: {
    kind: "read",
    icon: Users,
    verbs: ["Reading the client profile", "Read the client profile"]
  },
  list_client_cases: {
    kind: "read",
    icon: FolderOpen,
    verbs: ["Listing the client's cases", "Listed the client's cases"],
    counts: true
  },
  list_client_documents: {
    kind: "read",
    icon: FileText,
    verbs: ["Listing the client's documents", "Listed the client's documents"],
    counts: true
  },
  list_client_reminders: {
    kind: "read",
    icon: CalendarClock,
    verbs: ["Checking the client's calendar", "Checked the client's calendar"],
    counts: true
  },
  client_activity: {
    kind: "read",
    icon: ClipboardList,
    verbs: ["Reviewing client activity", "Reviewed client activity"]
  },
  create_reminder: {
    kind: "write",
    icon: CalendarClock,
    verbs: ["Adding a reminder", "Added a reminder"],
    target: (i) => str(i.title)
  },
  complete_reminder: {
    kind: "write",
    icon: CalendarClock,
    verbs: ["Completing a reminder", "Completed a reminder"]
  },
  update_client_profile: {
    kind: "write",
    icon: Users,
    verbs: ["Updating the client profile", "Updated the client profile"]
  },
  create_client: {
    kind: "write",
    icon: Users,
    verbs: ["Adding", "Added"],
    object: "a new client",
    target: (i) =>
      [str(i.first_name), str(i.last_name)].filter(Boolean).join(" ") || null
  },
  open_case: {
    kind: "write",
    icon: FolderPlus,
    verbs: ["Opening", "Opened"],
    object: "a new case",
    target: (i) => {
      const visa = str(i.visa_type);
      return visa ? `${withArticle(visa)} case` : null;
    }
  },
  propose_client_note: {
    kind: "write",
    icon: StickyNote,
    verbs: ["Adding to the client's notes", "Added to the client's notes"]
  },
  propose_change_set: {
    kind: "read",
    icon: Replace,
    verbs: [
      "Finding where the old value still appears",
      "Found where the old value still appears"
    ],
    target: (i) => str(i.field)?.replace(/_/g, " ") ?? null
  },
  list_client_records: {
    kind: "read",
    icon: ClipboardList,
    verbs: ["Reading the client's", "Read the client's"],
    object: "history",
    target: (i) => str(i.kind)?.replace(/_/g, " ") ?? null
  },
  add_client_record: {
    kind: "write",
    icon: ClipboardList,
    verbs: ["Adding to the client's", "Added to the client's"],
    object: "record",
    target: (i) => str(i.kind)?.replace(/_/g, " ") ?? null
  },
  update_client_record: {
    kind: "write",
    icon: ClipboardList,
    verbs: ["Updating a client", "Updated a client"],
    object: "record",
    target: (i) =>
      str(i.kind) ? `${str(i.kind)?.replace(/_/g, " ")} record` : null
  },
  delete_client_record: {
    kind: "write",
    icon: Trash2,
    verbs: ["Removing a client", "Removed a client"],
    object: "record",
    target: (i) =>
      str(i.kind) ? `${str(i.kind)?.replace(/_/g, " ")} record` : null
  },
  read_client_document: {
    kind: "read",
    icon: FileText,
    verbs: ["Reading a client document", "Read a client document"]
  },
  set_document_filing_status: {
    kind: "write",
    icon: FileInput,
    verbs: ["Marking a document", "Marked a document"],
    target: (i) => str(i.status)
  },
  reprocess_client_document: {
    kind: "run",
    icon: FileText,
    verbs: ["Reprocessing a document", "Reprocessed a document"]
  },
  send_documents_to_case: {
    kind: "write",
    icon: Send,
    verbs: ["Sending", "Sent"],
    object: "documents to a case",
    target: (i) => {
      const n = count(i.document_ids, "document");
      return n ? `${n} to a case` : null;
    }
  },
  delete_client_document: {
    kind: "write",
    icon: Trash2,
    verbs: ["Deleting a document", "Deleted a document"]
  },
  list_intake_templates: {
    kind: "read",
    icon: ClipboardList,
    verbs: ["Browsing intake templates", "Browsed intake templates"],
    counts: true
  },
  list_client_intakes: {
    kind: "read",
    icon: ClipboardList,
    verbs: ["Checking the client's intakes", "Checked the client's intakes"],
    counts: true
  },
  create_intake: {
    kind: "write",
    icon: ClipboardList,
    verbs: ["Drafting an intake", "Drafted an intake"],
    target: (i) => str(i.title)
  },
  send_intake: {
    kind: "write",
    icon: Mail,
    verbs: ["Sending an intake", "Sent an intake"],
    target: (i) =>
      str(i.recipient_email) ? `to ${str(i.recipient_email)}` : null
  },
  set_intake_reminders: {
    kind: "write",
    icon: Bell,
    verbs: ["Changing the reminders", "Changed the reminders"]
  },
  remind_intake: {
    kind: "write",
    icon: Mail,
    verbs: [
      "Reminding the client about an intake",
      "Reminded the client about an intake"
    ]
  },
  get_intake: {
    kind: "read",
    icon: ClipboardList,
    verbs: (i) =>
      str(i.template_id)
        ? ["Reading the template", "Read the template"]
        : ["Reading the intake", "Read the intake"]
  },
  // The intake builder runs under the call; its steps show beneath the row.
  build_intake: {
    kind: "run",
    icon: Sparkles,
    verbs: ["Building an intake", "Built an intake"],
    target: (i) => quoted(i.title)
  },
  revise_intake: {
    kind: "run",
    icon: Sparkles,
    verbs: ["Revising the intake", "Revised the intake"]
  },
  edit_intake: {
    kind: "write",
    icon: Pencil,
    verbs: (i) =>
      str(i.template_id)
        ? ["Editing the template", "Edited the template"]
        : ["Editing the intake", "Edited the intake"]
  },
  save_intake_as_template: {
    kind: "write",
    icon: LayoutTemplate,
    verbs: ["Saving as a firm template", "Saved as a firm template"],
    target: (i) => quoted(i.name)
  },
  read_intake_submission: {
    kind: "read",
    icon: ClipboardList,
    verbs: ["Reading the client's answers", "Read the client's answers"]
  },
  assess_intake_submission: {
    kind: "read",
    icon: ListChecks,
    verbs: [
      "Checking the answers against the record",
      "Checked the answers against the record"
    ]
  },
  review_intake_answers: {
    kind: "write",
    icon: ListChecks,
    verbs: (i) => {
      const n = count(i.decisions, "answer");
      return n
        ? [`Recording decisions on ${n}`, `Recorded decisions on ${n}`]
        : ["Recording review decisions", "Recorded review decisions"];
    }
  },
  request_intake_revision: {
    kind: "write",
    icon: Mail,
    verbs: ["Asking the client for changes", "Asked the client for changes"]
  },
  accept_intake_submission: {
    kind: "write",
    icon: ListChecks,
    verbs: ["Accepting the submission", "Accepted the submission"]
  },
  apply_intake_submission: {
    kind: "write",
    icon: ClipboardList,
    verbs: [
      "Applying the accepted answers to the record",
      "Applied the accepted answers to the record"
    ]
  },
  // --- Practice manager -----------------------------------------------
  firm_overview: {
    kind: "read",
    icon: ClipboardList,
    verbs: ["Reading the firm overview", "Read the firm overview"]
  },
  list_cases: {
    kind: "read",
    icon: FolderOpen,
    verbs: ["Listing cases", "Listed cases"],
    counts: true
  },
  upcoming_deadlines: {
    kind: "read",
    icon: CalendarClock,
    verbs: ["Checking upcoming deadlines", "Checked upcoming deadlines"]
  },
  team_workload: {
    kind: "read",
    icon: Users,
    verbs: ["Reviewing team workload", "Reviewed team workload"]
  },
  recent_activity: {
    kind: "read",
    icon: ClipboardList,
    verbs: ["Reviewing recent activity", "Reviewed recent activity"]
  },
  list_clients: {
    kind: "read",
    icon: Users,
    verbs: ["Listing clients", "Listed clients"],
    counts: true
  },
  work_status: {
    kind: "read",
    icon: ClipboardList,
    verbs: ["Checking on work in progress", "Checked on work in progress"]
  },
  create_client_reminder: {
    kind: "write",
    icon: CalendarClock,
    verbs: ["Adding a reminder", "Added a reminder"],
    target: (i) => str(i.title)
  },
  create_client_reminders: {
    kind: "write",
    icon: CalendarClock,
    verbs: ["Adding reminders", "Added reminders"],
    target: (i) => {
      const n = Array.isArray(i.targets) ? i.targets.length : 0;
      const title = str(i.title);
      const who = n ? `${n} client${n === 1 ? "" : "s"}` : null;
      return who && title ? `${title} for ${who}` : who ?? title;
    }
  },
  // --- Time (the acting person's own entries) --------------------------
  my_time: {
    kind: "read",
    icon: Clock,
    verbs: ["Checking your time", "Checked your time"],
    target: (i) => {
      const from = str(i.date_from);
      const to = str(i.date_to);
      return from && to && to !== from ? `${from} to ${to}` : from;
    }
  },
  case_time: {
    kind: "read",
    icon: Clock,
    verbs: ["Checking time on the case", "Checked time on the case"]
  },
  unreleased_days: {
    kind: "read",
    icon: Clock,
    verbs: ["Checking unreleased time", "Checked unreleased time"]
  },
  log_time: {
    kind: "write",
    icon: Timer,
    verbs: ["Logging time", "Logged time"],
    target: (i) => {
      const hours = typeof i.hours === "number" ? `${i.hours}h` : null;
      const what = quoted(i.narrative);
      return hours && what ? `${hours} ${what}` : hours ?? what;
    }
  },
  update_time_entry: {
    kind: "write",
    icon: Pencil,
    verbs: ["Updating a time entry", "Updated a time entry"]
  },
  merge_time_entries: {
    kind: "write",
    icon: ArrowRightLeft,
    verbs: ["Merging time entries", "Merged time entries"],
    target: (i) => entryCount(i.entry_ids)
  },
  split_time_entry: {
    kind: "write",
    icon: Scissors,
    verbs: ["Splitting a time entry", "Split a time entry"]
  },
  discard_time_entry: {
    kind: "write",
    icon: Trash2,
    verbs: ["Discarding time", "Discarded time"],
    target: (i) => entryCount(i.entry_ids)
  },
  release_time_entries: {
    kind: "write",
    icon: Send,
    verbs: ["Releasing time", "Released time"],
    target: (i) => entryCount(i.entry_ids)
  },
  add_expense: {
    kind: "write",
    icon: Receipt,
    verbs: ["Suggesting an expense", "Suggested an expense"],
    target: (i) => {
      const amount =
        typeof i.amount === "number"
          ? `$${i.amount.toLocaleString("en-US")}`
          : null;
      const kind = str(i.kind)?.replace(/_/g, " ") ?? null;
      return amount && kind ? `${amount} ${kind}` : amount ?? kind;
    }
  },
  // --- Pre-bill (firm admins) -----------------------------------------
  prebill_status: {
    kind: "read",
    icon: ListChecks,
    verbs: ["Checking pre-bill flags", "Checked pre-bill flags"]
  },
  apply_prebill_fix: {
    kind: "write",
    icon: Wrench,
    verbs: ["Applying a pre-bill fix", "Applied a pre-bill fix"]
  },
  dismiss_prebill_flag: {
    kind: "write",
    icon: AlertTriangle,
    verbs: ["Dismissing a pre-bill flag", "Dismissed a pre-bill flag"],
    target: (i) => quoted(i.reason)
  },
  // --- Companies -------------------------------------------------------
  company_profile: {
    kind: "read",
    icon: Building2,
    verbs: ["Reading the company record", "Read the company record"]
  },
  propose_playbook_rule: {
    kind: "write",
    icon: ScrollText,
    verbs: ["Suggesting a playbook rule", "Suggested a playbook rule"],
    target: (i) => quoted(i.text)
  },
  link_case_company: {
    kind: "write",
    icon: Building2,
    verbs: ["Setting the petitioner to", "Set the petitioner to"],
    object: "a company",
    // A name reads; a bare record id would not.
    target: (i) => {
      const value = str(i.company_id);
      return value && /\s/.test(value) ? value : null;
    }
  },
  request_company_approval: {
    kind: "write",
    icon: Building2,
    verbs: ["Asking the company to approve", "Asked the company to approve"],
    target: (i) => quoted(i.summary)
  },
  message_company: {
    kind: "write",
    icon: Mail,
    verbs: ["Writing to the company's HR", "Wrote to the company's HR"],
    target: (i) => quoted(i.body)
  },
  // --- Skill builder ---------------------------------------------------
  list_firm_skills: {
    kind: "read",
    icon: Sparkles,
    verbs: ["Listing the firm's automations", "Listed the firm's automations"]
  },
  read_firm_skill: {
    kind: "read",
    icon: Sparkles,
    verbs: ["Reading an automation", "Read an automation"]
  },
  list_agent_capabilities: {
    kind: "read",
    icon: ListChecks,
    verbs: [
      "Checking what the assistant can do",
      "Checked what the assistant can do"
    ]
  },
  browse_firm_library: {
    kind: "read",
    icon: BookOpen,
    verbs: ["Browsing the firm's library", "Browsed the firm's library"]
  },
  read_library_entry: {
    kind: "read",
    icon: BookOpen,
    verbs: ["Reading a library entry", "Read a library entry"]
  },
  read_thread_transcript: {
    kind: "read",
    icon: MessageCircleQuestion,
    verbs: ["Reading the conversation", "Read the conversation"]
  },
  list_skill_runs: {
    kind: "read",
    icon: Activity,
    verbs: ["Checking the automation's runs", "Checked the automation's runs"]
  },
  read_skill_run: {
    kind: "read",
    icon: Activity,
    verbs: ["Reading an automation run", "Read an automation run"]
  },
  create_firm_skill: {
    kind: "write",
    icon: Sparkles,
    verbs: ["Writing the automation", "Wrote the automation"],
    target: (i) => quoted(i.title)
  },
  update_firm_skill: {
    kind: "write",
    icon: Pencil,
    verbs: ["Changing the automation", "Changed the automation"],
    target: (i) => quoted(i.title) ?? quoted(i.change_note)
  },
  // --- Settings and onboarding ---------------------------------------
  save_my_profile: {
    kind: "write",
    icon: UserCog,
    verbs: ["Updating your profile", "Updated your profile"]
  },
  save_my_preferences: {
    kind: "write",
    icon: SlidersHorizontal,
    verbs: (i) => {
      const section = SETTINGS_SECTION_NAMES[str(i.section) ?? ""];
      return section
        ? [`Updating your ${section}`, `Updated your ${section}`]
        : ["Updating your preferences", "Updated your preferences"];
    }
  },
  save_firm_settings: {
    kind: "write",
    icon: Building2,
    verbs: (i) => {
      const section = SETTINGS_SECTION_NAMES[str(i.section) ?? ""];
      return section
        ? [`Updating ${section}`, `Updated ${section}`]
        : ["Updating the firm's settings", "Updated the firm's settings"];
    }
  },
  get_setup_state: {
    kind: "read",
    icon: ListChecks,
    verbs: ["Checking your setup", "Checked your setup"]
  },
  prefill_from_connected_apps: {
    kind: "own",
    icon: ScanSearch,
    verbs: (i) =>
      Array.isArray(i.sources) && i.sources.includes("calendar")
        ? Array.isArray(i.sources) && i.sources.includes("signature")
          ? [
              "Reading your email signature and calendar",
              "Read your email signature and calendar"
            ]
          : ["Reading your calendar", "Read your calendar"]
        : ["Reading your email signature", "Read your email signature"]
  },
  star_clients: {
    kind: "write",
    icon: Star,
    verbs: ["Starring", "Starred"],
    object: "client channels",
    target: (i) => count(i.client_ids, "client channel")
  },
  show_suggested_agents: {
    kind: "own",
    icon: Zap,
    verbs: ["Showing the firm's agents", "Showed the firm's agents"]
  },
  show_firm_setup: {
    kind: "own",
    icon: Building2,
    verbs: ["Checking firm setup", "Checked firm setup"]
  },
  skip_setup_item: {
    kind: "write",
    icon: SkipForward,
    verbs: ["Skipping", "Skipped"],
    target: (i) => str(i.item)?.replace(/_/g, " ") ?? null
  },
  note_setup_answers: {
    kind: "write",
    icon: StickyNote,
    verbs: ["Noting your answers", "Noted your answers"]
  }
};

/** Tools whose purpose-built card replaces the generic row. */
export const OWN_CARD_TOOLS: ReadonlySet<string> = new Set(
  Object.entries(ENTRIES)
    .filter(([, e]) => e.kind === "own")
    .map(([name]) => name)
);

const humanise = (name: string): string => {
  const words = name.replace(/[_-]+/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : "Tool";
};

/** The first short string argument, for tools without an entry. */
const firstStringArg = (input: Input): string | null => {
  for (const v of Object.values(input)) {
    const s = str(v);
    if (s && s.length <= 80 && !/^[a-z0-9_-]{16,}$/i.test(s)) return s;
  }
  return null;
};

const NO_RESOLVE: ArtifactResolver = () => null;

/** How a call to `name` with `input` (and its `output`, once it has one) reads, running and done. */
export const describeTool = (
  name: string,
  input: unknown,
  resolve: ArtifactResolver = NO_RESOLVE,
  output?: unknown
): ToolTitle => {
  const args = isRecord(input) ? input : {};
  const entry = ENTRIES[name];
  if (!entry) {
    const label = humanise(name);
    const target = firstStringArg(args);
    const text = target ? `${label} · ${clip(target, 48)}` : label;
    return {
      kind: "read",
      icon: Wrench,
      present: text,
      past: text,
      target,
      search: false
    };
  }
  const [present, past] =
    typeof entry.verbs === "function" ? entry.verbs(args, output) : entry.verbs;
  let target: string | null = null;
  try {
    target = entry.target?.(args, resolve) ?? null;
  } catch {
    target = null;
  }
  const tail = target ?? entry.object ?? null;
  return {
    kind: entry.kind,
    icon: entry.icon,
    present: tail ? `${present} ${tail}` : present,
    past: tail ? `${past} ${tail}` : past,
    target,
    search: Boolean(entry.search)
  };
};

/**
 * A list's finished title with what it returned, in Cursor's words: "Listed"
 * + "12 clients", "Checked" + "3 intakes". Null for other tools and for one
 * result, which keep their own title.
 */
export const countedTitle = (
  name: string,
  title: Pick<ToolTitle, "past">,
  entities: readonly { target: { kind: string } }[]
): string | null => {
  if (!ENTRIES[name]?.counts || entities.length < 2) return null;
  const verb = title.past.split(" ")[0];
  const kinds = new Set(entities.map((e) => e.target.kind));
  const noun = kinds.size === 1 ? `${[...kinds][0]}s` : "results";
  return `${verb} ${entities.length} ${noun}`;
};

/** The present-tense title alone (for status lines and error headers). */
export const presentTitle = (
  name: string,
  input?: unknown,
  resolve?: ArtifactResolver
): string => describeTool(name, input, resolve).present;

/** The layout kind of a tool, without needing its input. */
export const toolKind = (name: string): ToolKind =>
  ENTRIES[name]?.kind ?? "read";

/** Read-only calls show a plain title (and links when present), not raw JSON. */
export const showRawCallDetails = (name: string): boolean =>
  toolKind(name) !== "read";

/** Run tools whose failed row hides tool arguments (the failure card carries context). */
const RUN_TOOLS_HIDE_INPUT_ON_ERROR = new Set([
  "generate_packet",
  "export_filing_materials",
  "fill_form",
  "start_autocategorize",
  "start_pdf_edit",
  "draft_pdf_changes",
  "propose_exhibit_titles",
  "build_intake",
  "revise_intake",
  "reprocess_client_document",
  "task",
  "delegate_to_assistant",
  "handoff_to_agent",
  "execute"
]);

export const hideRunToolInputOnError = (name: string): boolean =>
  RUN_TOOLS_HIDE_INPUT_ON_ERROR.has(name);

const RUN_ERROR_ROW: Record<string, string> = {
  generate_packet: "Couldn't assemble the packet",
  export_filing_materials: "Couldn't export filing materials",
  fill_form: "Couldn't fill the form",
  start_autocategorize: "Couldn't run the organizer",
  start_pdf_edit: "Couldn't finish the PDF edit",
  draft_pdf_changes: "Couldn't draft PDF changes",
  propose_exhibit_titles: "Couldn't propose titles",
  generate_draft: "Couldn't finish drafting",
  start_research: "Couldn't finish research",
  build_intake: "Couldn't build the intake",
  revise_intake: "Couldn't revise the intake",
  reprocess_client_document: "Couldn't reprocess the document",
  task: "Couldn't finish exploring",
  delegate_to_assistant: "Couldn't finish",
  handoff_to_agent: "Handoff failed",
  execute: "Command failed"
};

/** Past-tense row title when a run tool failed (not the in-progress present line). */
export const errorRowTitle = (name: string, title: ToolTitle): string => {
  if (title.kind !== "run") return title.present;
  return RUN_ERROR_ROW[name] ?? "The run failed";
};

/** A run tool's error string as the attorney should read it in the row fold. */
export const friendlyToolError = (name: string, error: string): string => {
  const text = error.trim();
  if (name === "generate_packet" || name === "export_filing_materials") {
    const formNotStarted = /form\s+(\S+)\s+is\s+not_started/i.exec(text);
    if (formNotStarted) {
      return `Form ${formNotStarted[1]} hasn't been filled yet. Fill it before assembling the packet.`;
    }
    if (/upload names/i.test(text)) {
      const count = /^(\d+)\s+included/i.exec(text);
      if (count) {
        const n = count[1];
        return `${n} included file${n === "1" ? "" : "s"} still ${
          n === "1" ? "has" : "have"
        } upload names. Retitle them before building the packet.`;
      }
      return "Some exhibits still have upload names. Retitle them before building the packet.";
    }
    if (/already being assembled/i.test(text)) {
      return "A packet build is already in progress. Wait for it to finish.";
    }
    if (/not ready to file/i.test(text)) {
      return "The case isn't ready to file yet. Fix the blockers Casey listed, then try again.";
    }
    if (/the packet build failed/i.test(text)) {
      return "The packet build failed. Check filing readiness and try again.";
    }
  }
  return friendlyRunFailure(text);
};

/** Title of a fold of consecutive read-only calls: "Explored 3 files, 2 searches". */
export const foldTitle = (
  titles: readonly Pick<ToolTitle, "search">[],
  running: boolean
): string => {
  const searches = titles.filter((t) => t.search).length;
  const reads = titles.length - searches;
  const parts = [
    reads ? `${reads} file${reads === 1 ? "" : "s"}` : null,
    searches ? `${searches} search${searches === 1 ? "" : "es"}` : null
  ].filter(Boolean);
  if (running) return "Exploring";
  return `Explored ${parts.join(", ")}`;
};
